import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSetAtom } from 'jotai';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import {
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
} from '../../Atoms';
import useTranslate from '../../Hooks/useTranslate';
import capitalize from '../../Helpers/StringsFormatting/capitalize';
import { renderColumnValue } from '../engine/columns/resolveCard';
import { ColumnFlexBox } from '../FlexBox';
import DbeAvatar from '../DbeAvatar';
import CallStatusChip from '../ContactListWrapper/EntryComponent/CallStatusChip';

/**
 * ContactCard — customer list row for the Contatti tab (see MOBILE_PLAN.md
 * §F(3)). Tap opens the customer overlay. The avatar shows a person icon, or a
 * red "blocked" icon when the customer is inactive (AllCustomers,
 * `active === false`).
 *
 * `card` is a column layout compiled by `resolveCard`: its `primary`,
 * `secondary` and `trailing` slots are drawn through the same cells the desktop
 * table uses, so a section configures both from one layout. Without it the card
 * falls back to the name, the mobile number and the call status chip.
 */
const ContactCard = function ContactCard({
  data,
  showInactive = false,
  sectionIcon: SectionIcon = null,
  card = null,
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const setCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);

  const inactive = showInactive && data?.active === false;
  const avatarIcon = inactive ? (
    <BlockOutlinedIcon />
  ) : SectionIcon ? (
    <SectionIcon />
  ) : (
    <PersonOutlinedIcon />
  );

  const slot = (name) =>
    card?.[name] ? renderColumnValue(card[name], data, translate) : null;

  const primary = slot('primary');
  const secondary = slot('secondary');
  const trailing = slot('trailing');

  const openCustomer = () => {
    setCustomerId(data?.id);
    setAdditionalCustomerData({
      customerName: data?.fullName,
      wavePeriod: null,
    });
  };

  return (
    <Paper
      onClick={openCustomer}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.25,
        cursor: 'pointer',
        opacity: data?.removedOn ? 0.55 : 1,
      }}
    >
      <DbeAvatar
        size={36}
        color={inactive ? theme.palette.error.main : theme.palette.primary.main}
        tooltip={inactive ? translate('INACTIVE') : ''}
        icon={avatarIcon}
      />
      <ColumnFlexBox sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Typography sx={{ fontWeight: 600 }} noWrap component="div">
          {primary ??
            capitalize(data?.fullName) ??
            translate('CONTACT_UNAVAILABLE')}
        </Typography>
        <Typography
          variant="body2"
          noWrap
          component="div"
          sx={{
            color: theme.custom.mobile.mutedColor,
            overflow: 'visible',
          }}
        >
          {secondary ?? data?.mobilePhoneNumber}
        </Typography>
      </ColumnFlexBox>
      <Box
        sx={{
          flex: 'none',
          // The slot holds whatever cell the layout put there, so it is a column of its own width
          // rather than whatever the cell happens to measure - every card's trailing value then
          // starts at the same place, as the list's status column does. A cell that lays its own
          // parts out in a row is told to hug the right, or a wrapped label leaves slack beside it.
          ml: 'auto',
          width: '112px',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          '& > *': { justifyContent: 'flex-end' },
          // Fixed width so every status chip is equal; long labels truncate.
          '& .MuiChip-root': {
            width: '104px !important',
            height: '26px !important',
            fontSize: '11px !important',
          },
          '& .MuiChip-label': { px: '8px !important' },
          '& .MuiChip-icon': {
            fontSize: '16px !important',
            ml: '6px !important',
            mr: '-4px !important',
          },
        }}
      >
        {trailing ?? <CallStatusChip data={data} />}
      </Box>
    </Paper>
  );
};

export default ContactCard;
